<template>
  <component
    :is="to ? 'router-link' : 'button'"
    class="button-wrapper button"
    :class="[
      BASE, PADDING[padding],
      red ? VARIANT.red : VARIANT[variant],
      pressed && PRESSED[variant],
      { disabled, pressed, red },
    ]"
    v-bind="to ? { to } : { disabled }"
    @click="onClick"
    @mouseenter="stopCountdown"
    ref="btnEl"
  >
    <div
      class="button-countdown-progress absolute inset-y-0 right-0 z-0 bg-line/60"
      v-if="autoClickAfter !== 0"
      :style="{ width: `${autoClickTimerProgress}%` }"
    ></div>
    <Spinner v-if="loading" :size="1.5" class="icon"></Spinner>
    <slot name="default" v-if="slots.default"></slot>
    <template v-else>
      <slot name="icon" v-if="!loading && icon">
        <i :class="`tr tr-${icon} ${iconSize} ${!text ? 'no-text' : ''} icon simple-button-text`"></i>
      </slot>
      <slot name="text">
        <div v-if="text" class="text z-1 whitespace-nowrap text-center">
          {{ text }}
        </div>
      </slot>
    </template>
    <span v-if="showCountdown && autoClickAfter && counting" class="z-1 font-normal tabular-nums opacity-70">
      {{ Math.ceil(timeLeft) }}s
    </span>
  </component>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, useSlots } from 'vue';
import { useEventListener } from '@vueuse/core';
import Spinner from '@/components/elements/Spinner';
const slots = useSlots();

const props = defineProps({
  icon: String,
  text: String,
  to: [String, Object],
  iconSize: {
    type: String,
    default: 'medium',
  },
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'accent', 'ghost', 'green', 'amber', 'swatch', 'danger', 'red'].includes(value),
  },
  // `auto` leaves the caller's own px-*/py-* classes in charge, which most call
  // sites rely on; it only adds padding when the `text` prop renders the label.
  size: {
    type: String,
    default: 'auto',
    validator: (value) => ['auto', 'xs', 'sm', 'md', 'lg', 'icon'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  red: {
    type: Boolean,
    default: false,
  },
  pressed: {
    type: Boolean,
    default: false,
  },
  autoClickAfter: {
    type: Number,
    default: 0,
  },
  showCountdown: {
    type: Boolean,
    default: false,
  },
  stopOnWindowHover: {
    type: Boolean,
    default: false,
  },
});

const BASE = `relative inline-flex shrink-0 items-center justify-center overflow-hidden
  cursor-pointer select-none whitespace-nowrap rounded-control font-medium
  transition-[background-color,border-color,color] duration-150 ease-in-out
  focus:outline-none focus-visible:shadow-[var(--ring)]
  disabled:opacity-45 disabled:pointer-events-none`;

// The control height scale: 28 / 32 / 36 / 40.
// `icon` is not a rung - it is a square, sized by its glyph.
const PADDING = {
  auto: '',
  xs: 'gap-1.5 min-h-7 px-2.5 text-label',
  sm: 'gap-2 h-8 min-h-8 px-3 text-label',
  md: 'gap-2 min-h-9 px-3.5 text-sm',
  lg: 'gap-2.5 min-h-10 min-w-22 px-3.75 py-1.25 text-sm',
  icon: 'gap-0 p-2',
};

const VARIANT = {
  accent: 'border-0 bg-accent text-on-accent font-bold hover:bg-accent-hover hover:shadow-[var(--accent-glow)]',
  primary: 'border border-line-control bg-input light:bg-input-solid text-text hover:bg-active hover:border-accent hover:text-accent',
  ghost: 'border border-transparent bg-transparent text-text-muted hover:bg-hover hover:text-accent',
  green: 'border-0 bg-green text-on-accent hover:brightness-110',
  amber: 'border-0 bg-amber text-on-accent hover:brightness-110',
  // background is caller-supplied data (e.g. a label colour) via :style
  swatch: 'border-0 bg-neutral text-white hover:brightness-110',
  red: 'border-0 bg-red text-on-red hover:bg-red-hover',
  danger: 'border border-red/35 bg-transparent text-red hover:border-red hover:bg-red-soft',
};

const PRESSED = {
  primary: 'bg-active! border-accent! text-accent!',
  ghost: 'bg-active! text-accent!',
  green: 'brightness-90!',
  amber: 'brightness-90!',
  swatch: 'brightness-90!',
};

const emit = defineEmits(['click']);

const timeLeft = ref(0);
const counting = ref(true);
let countDownInterval = null;

const btnEl = ref(null);

// `text` renders the built-in label, which needs the button to carry its own padding
const padding = computed(() => (props.size === 'auto' && props.text ? 'lg' : props.size));

const onClick = (auto = false) => {
  if (!props.disabled) {
    emit('click', auto);
  }
};

// a hovered auto-click never fires: the user is reading and decides themselves
const stopCountdown = () => {
  counting.value = false;
  timeLeft.value = 0;
  if (countDownInterval) {
    clearInterval(countDownInterval);
  }
};

// popup windows: the mouse anywhere over the window is a hover of the popup
if (props.stopOnWindowHover) {
  useEventListener(document, 'mousemove', stopCountdown, { once: true });
}

defineExpose({ stopCountdown });

const autoClickTimerProgress = computed(() => {
  return (timeLeft.value * 100) / props.autoClickAfter;
});

onMounted(() => {
  if (props.autoClickAfter) {
    timeLeft.value = props.autoClickAfter;
    const steps = 1000.0;
    const intervalSeconds = props.autoClickAfter / steps;
    countDownInterval = setInterval(() => {
      if (counting.value) {
        timeLeft.value -= intervalSeconds;
      }
      if (timeLeft.value < 0) {
        clearInterval(countDownInterval);
        onClick(true);
      }
    }, intervalSeconds * 1000);
  }
});

onUnmounted(() => {
  clearInterval(countDownInterval);
});
</script>

<style lang="scss">
/* Not scoped, so parents can override layout (9 files target .button-wrapper).
   In @layer components so utilities on <SimpleButton> win — see assets/tailwind.css. */
@layer components {

// keep slot content above the countdown progress bar
.button-wrapper > :not(.button-countdown-progress) {
  position: relative;
  z-index: 1;
}

}
</style>
